import React, { useEffect, useMemo, useRef, useState } from 'react';
import JoditEditor from 'jodit-react';

/**
 * Email clients apply default margins to `<p>`; Jodit only zeros margins inside `.jodit-wysiwyg`.
 * Merge margin/padding resets into inline styles so sent HTML matches the editor.
 */
export function normalizeEmailHtmlForSending(html) {
  if (html === null || html === undefined) return '';
  const safe = String(html);
  if (!safe.trim()) return safe;

  try {
    const doc = new DOMParser().parseFromString(
      `<div id="tms-email-normalize-root">${safe}</div>`,
      'text/html'
    );
    const root = doc.getElementById('tms-email-normalize-root');
    if (!root) return safe;

    const scope = root.querySelector('.tms-email-body') || root;
    scope.querySelectorAll('p').forEach((p) => {
      const existing = (p.getAttribute('style') || '').trim();
      let next = existing;
      if (!/margin\s*:/i.test(next)) {
        next = next ? `${next};margin:0` : 'margin:0';
      }
      if (!/padding\s*:/i.test(next)) {
        next = `${next};padding:0`;
      }
      p.setAttribute('style', next);
    });

    return root.innerHTML;
  } catch {
    return safe;
  }
}

const EmailComponent = ({ formData, setFormData }) => {
  const editor = useRef(null);
  const [content, setContent] = useState(formData?.template_content || '');
  const [dirty, setDirty] = useState(false);

  const sanitizePastedHtml = (html) => {
    if (!html || typeof html !== 'string') return html;

    const ALLOWED_TAGS = new Set([
      'p',
      'br',
      'strong',
      'b',
      'em',
      'i',
      'u',
      's',
      'del',
      'sub',
      'sup',
      'ul',
      'ol',
      'li',
      'a',
      'table',
      'thead',
      'tbody',
      'tfoot',
      'tr',
      'td',
      'th',
      'colgroup',
      'col',
    ]);

    const UNWRAP_TAGS = new Set([
      'font',
      'span',
      'div',
      'section',
      'article',
      'header',
      'footer',
      'main',
      'nav',
      'aside',
      'center',
      'blockquote',
      'pre',
      'code',
    ]);

    const DROP_SELECTOR =
      'script,style,noscript,iframe,object,embed,meta,link,head,title,base,' +
      'img,video,audio,picture,source,track,form,input,button,select,textarea,canvas,svg';

    /** @type {Record<string, string[]>} */
    const ATTR_ALLOW = {
      a: ['href', 'target', 'rel', 'title'],
      table: ['border', 'cellpadding', 'cellspacing'],
      td: ['colspan', 'rowspan'],
      th: ['colspan', 'rowspan', 'scope'],
    };

    const unwrapElement = (el) => {
      const parent = el.parentNode;
      if (!parent) return;
      while (el.firstChild) {
        parent.insertBefore(el.firstChild, el);
      }
      parent.removeChild(el);
    };

    const normalizedText = (text) => (text || '').replace(/\u00a0/g, ' ').trim();

    const isEmptyParagraph = (el) => {
      if (el.nodeType !== Node.ELEMENT_NODE || el.tagName !== 'P') return false;
      if (normalizedText(el.textContent || '').length > 0) return false;
      for (let i = 0; i < el.childNodes.length; i += 1) {
        const child = el.childNodes[i];
        if (child.nodeType === Node.TEXT_NODE) continue;
        if (child.nodeType === Node.ELEMENT_NODE) {
          if (child.tagName !== 'BR') return false;
        }
      }
      return true;
    };

    const trimMeaninglessTextNodes = (container) => {
      const isWsOnlyText = (node) =>
        node.nodeType === Node.TEXT_NODE && normalizedText(node.textContent || '').length === 0;

      while (container.firstChild && isWsOnlyText(container.firstChild)) {
        container.firstChild.remove();
      }
      while (container.lastChild && isWsOnlyText(container.lastChild)) {
        container.lastChild.remove();
      }
    };

    const trimFragmentEdges = (container) => {
      trimMeaninglessTextNodes(container);
      while (container.firstChild) {
        const fc = container.firstChild;
        if (fc.nodeType === Node.ELEMENT_NODE && fc.tagName === 'BR') {
          fc.remove();
          continue;
        }
        if (fc.nodeType === Node.ELEMENT_NODE && isEmptyParagraph(fc)) {
          fc.remove();
          continue;
        }
        break;
      }
      while (container.lastChild) {
        const lc = container.lastChild;
        if (lc.nodeType === Node.ELEMENT_NODE && lc.tagName === 'BR') {
          lc.remove();
          continue;
        }
        if (lc.nodeType === Node.ELEMENT_NODE && isEmptyParagraph(lc)) {
          lc.remove();
          continue;
        }
        break;
      }
      trimMeaninglessTextNodes(container);
    };

    const collapseConsecutiveEmptyParagraphs = (container) => {
      const stack = [container];
      while (stack.length) {
        const parent = stack.pop();
        if (!parent) continue;
        let child = parent.firstChild;
        while (child) {
          if (child.nodeType === Node.ELEMENT_NODE) {
            stack.push(child);
          }
          if (child.nodeType === Node.ELEMENT_NODE && child.tagName === 'P' && isEmptyParagraph(child)) {
            let scan = child.nextSibling;
            while (
              scan &&
              scan.nodeType === Node.ELEMENT_NODE &&
              scan.tagName === 'P' &&
              isEmptyParagraph(scan)
            ) {
              const toRemove = scan;
              scan = scan.nextSibling;
              toRemove.remove();
            }
          }
          child = child.nextSibling;
        }
      }
    };

    const scrubAttributes = (rootEl) => {
      rootEl.querySelectorAll('*').forEach((el) => {
        const tag = el.tagName.toLowerCase();
        const allowed = ATTR_ALLOW[tag];
        const attrs = Array.from(el.attributes);
        attrs.forEach((attr) => {
          const nameLower = attr.name.toLowerCase();
          if (!allowed || !allowed.includes(nameLower)) {
            el.removeAttribute(attr.name);
          }
        });
        if (tag === 'a') {
          const target = (el.getAttribute('target') || '').toLowerCase();
          if (target === '_blank') {
            const rel = (el.getAttribute('rel') || '').toLowerCase();
            if (!rel.includes('noopener') || !rel.includes('noreferrer')) {
              const parts = (el.getAttribute('rel') || '')
                .split(/\s+/)
                .filter(Boolean);
              if (!rel.includes('noopener')) parts.push('noopener');
              if (!rel.includes('noreferrer')) parts.push('noreferrer');
              el.setAttribute('rel', [...new Set(parts)].join(' '));
            }
          }
        }
      });
    };

    const root = document.createElement('div');
    root.innerHTML = html;

    root.querySelectorAll(DROP_SELECTOR).forEach((el) => el.remove());

    root.querySelectorAll('h1,h2,h3,h4,h5,h6').forEach((heading) => {
      const p = document.createElement('p');
      while (heading.firstChild) {
        p.appendChild(heading.firstChild);
      }
      heading.parentNode?.replaceChild(p, heading);
    });

    let unwrapPass = 0;
    while (unwrapPass < 20) {
      unwrapPass += 1;
      let changed = false;
      root.querySelectorAll('*').forEach((el) => {
        const tag = el.tagName.toLowerCase();
        if (UNWRAP_TAGS.has(tag)) {
          unwrapElement(el);
          changed = true;
        }
      });
      if (!changed) break;
    }

    for (let pass = 0; pass < 5; pass += 1) {
      let changed = false;
      root.querySelectorAll('*').forEach((el) => {
        const tag = el.tagName.toLowerCase();
        if (!ALLOWED_TAGS.has(tag)) {
          unwrapElement(el);
          changed = true;
        }
      });
      if (!changed) break;
    }

    scrubAttributes(root);

    trimFragmentEdges(root);
    collapseConsecutiveEmptyParagraphs(root);
    trimFragmentEdges(root);

    return root.innerHTML;
  };

  // Keep editor in sync with incoming template content, but never overwrite once user starts editing.
  useEffect(() => {
    if (!dirty) {
      setContent(formData?.template_content || '');
    }
  }, [formData?.template_content, dirty]);

  const baseEmailBodyCSS = useMemo(
    () =>
      [
        // Base typography for email composing
        'body, .jodit-wysiwyg { font-family: "Khmer UI", Geneva, sans-serif !important; font-size: 16px !important; line-height: 1.4; }',
        // Prevent large gaps when `enter: "P"` creates paragraphs
        '.jodit-wysiwyg p { margin: 0; }',
        // Keep lists usable/consistent
        '.jodit-wysiwyg ul, .jodit-wysiwyg ol { margin: 0; padding-left: 1.25rem; }',
        '.jodit-wysiwyg li { margin: 0; }',
        // Ensure unstyled spans inherit editor base
        '.jodit-wysiwyg span { font-family: inherit; font-size: inherit; line-height: inherit; }',
      ].join('\n'),
    []
  );

  const config = useMemo(
    () => ({
      readonly: false,
      height: 400,
      toolbarAdaptive: false,
      showCharsCounter: false,
      showWordsCounter: false,
      showXPathInStatusbar: false,
      toolbarSticky: false,
      // Paste behavior: no popup, clean HTML automatically
      askBeforePasteHTML: false,
      processPasteHTML: true,
      defaultActionOnPaste: 'tms_email_paste',
      enter: 'P',
      nl2br: false,
      cleanHTML: {
        fillEmptyParagraph: false,
      },
      events: {
        onCustomPasteHTMLOption: (action, html) => {
          if (action === 'tms_email_paste') {
            return sanitizePastedHtml(html);
          }
          return html;
        },
      },
      placeholder: '',
      // Works for both iframe and non-iframe modes (Jodit uses one depending on config/version).
      style: baseEmailBodyCSS,
      iframeStyle: baseEmailBodyCSS,
      buttons: [
        'bold', 'italic', 'underline', '|',
        'ul', 'ol', '|',
        'outdent', 'indent', '|',
        'font', 'fontsize', 'brush', 'paragraph', '|',
        'image', 'video', 'table', 'link', '|',
        'align', 'undo', 'redo', '|',
        'hr', 'copyformat', 'symbol', 'print', 'about', '|',
        'fullsize', 'source'
      ]
    }),
    [baseEmailBodyCSS]
  );

  return (
    <div className="tmsEmailEditor">
      <JoditEditor
        ref={editor}
        value={content}
        config={config}
        onChange={(newContent) => {
          setDirty(true);
          setContent(newContent);
          setFormData((prev) => ({ ...prev, template_content: newContent }));
        }}
        onBlur={(newContent) => {
          // Keep blur as a final sync point (some browsers/editors apply changes on blur).
          setDirty(true);
          const normalized = normalizeEmailHtmlForSending(newContent);
          setContent(normalized);
          setFormData((prev) => ({ ...prev, template_content: normalized }));
        }}
      />
    </div>
  );
};

export default EmailComponent;
